import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { EMPTY, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, finalize, switchMap, take, tap } from 'rxjs/operators';

import { REFRESH_TOKEN_NAME, TOKEN_NAME } from '@app/constants/constants';
import { AuthService } from '@app/services/auth.service';
import { CookieService } from '@app/services/cookie.service';

// Module-level state shared across all interceptor invocations.
let refreshInProgress = false;
// Emits the outcome of the in-flight refresh: true = succeeded, false = failed.
// Carrying the outcome ensures queued requests are always released, even on failure.
const tokenRefreshedSource = new Subject<boolean>();

/**
 * Auth interceptor. Attaches Bearer token to every request.
 * On 401, attempts token refresh once, then retries.
 * Falls back to cookie-based login if refresh fails.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const cookieService = inject(CookieService);
    const authService = inject(AuthService);

    const addAuthHeader = (r: typeof req) => {
        const token = cookieService.get(TOKEN_NAME);
        if (!token) return r;
        return r.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    };

    // Waits for the in-flight refresh to settle. Resolves when it succeeds,
    // errors when it fails — so a failed refresh never leaves the request hanging.
    const waitForRefresh = (): Observable<void> =>
        tokenRefreshedSource.pipe(
            take(1),
            switchMap((succeeded) => (succeeded ? of<void>(undefined) : throwError(() => new Error('Token refresh failed')))),
        );

    const doRefresh = (): Observable<void> => {
        if (refreshInProgress) return waitForRefresh();

        refreshInProgress = true;
        return authService.refreshToken().pipe(
            tap(() => tokenRefreshedSource.next(true)),
            switchMap(() => of<void>(undefined)),
            catchError(() => {
                const loginCookie = cookieService.get('mst_logged_id');
                if (loginCookie) {
                    return authService.loginCookie(loginCookie).pipe(
                        tap(() => tokenRefreshedSource.next(true)),
                        switchMap(() => of<void>(undefined)),
                        catchError(() => {
                            tokenRefreshedSource.next(false);
                            authService.goToLogin();
                            return EMPTY;
                        }),
                    );
                }
                tokenRefreshedSource.next(false);
                authService.goToLogin();
                return EMPTY;
            }),
            finalize(() => (refreshInProgress = false)),
        );
    };

    const isAuthUrl = req.url.includes('login') || req.url.includes('token/refresh') || req.url.includes('validate_user_token');

    return next(addAuthHeader(req)).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !isAuthUrl && cookieService.get(REFRESH_TOKEN_NAME)) {
                return doRefresh().pipe(
                    switchMap(() => next(addAuthHeader(req))),
                    catchError(() => {
                        authService.goToLogin();
                        return EMPTY;
                    }),
                );
            }
            return throwError(() => error);
        }),
    );
};
